import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'

import ItemsLoading from '../../../../components/Loaders/ItemsLoading'
import SkeletonListRow from '../../../../features/components/List/Skeleton'
import PageSection from '../../../../features/components/PageSection'
import List from '../../../../features/components/List'
import Chip from '../../../../components/Chip'
import ActionsDropdown from '../../../../features/components/ActionsDropdown'

import { showToasts } from '../../../../helpers/toasts'

import {
  deletedInvitationSelector,
  invitesErrorSelector,
  invitesIsLoadingSelector,
  invitesSelector,
  invitesWasLoadedSelector,
  reinvitedUserSelector
} from '../../../../modules/selectors/team'
import { deleteInvite, resendInvite } from '../../../../modules/actions/team'

import { CHIP_COLORS, TOAST_TYPE } from '../../../../constants/other'
import { listItemsTableColumnsMinWidth } from '../../../../features/components/ListItemsTable/columnsWidth'

import useCommonStyles from '../../../../styles/common/listPage'

const InvitesList = () => {
  const commonClasses = useCommonStyles()
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const invitesLists = useSelector(invitesSelector)
  const { id: invitationId, isLoading: invitationLoading } = useSelector(reinvitedUserSelector)
  const { id: deleteInvitationId, isLoading: deleteInvitationLoading } = useSelector(deletedInvitationSelector)

  const resendInviteHandler = useCallback(
    id => {
      dispatch(resendInvite(id))
      showToasts({
        type: TOAST_TYPE.success,
        message: t('The invitation has been sent via email')
      })
    },
    [t, dispatch]
  )

  const deleteInviteHandler = useCallback(
    id => {
      dispatch(deleteInvite(id))
    },
    [dispatch]
  )

  const getInvitesActionsDropdownOptions = useCallback(
    invite => {
      const { id } = invite

      return [
        {
          text: t('Resend invite'),
          onClickHandler: () => resendInviteHandler(id)
        },
        {
          text: t('Delete invitation'),
          onClickHandler: () => deleteInviteHandler(id)
        }
      ]
    },
    [t, deleteInviteHandler, resendInviteHandler]
  )

  const invitesListColumns = useMemo(
    () => [
      {
        headTitle: 'Invites',
        field: 'email',
        style: {
          minWidth: 250
        }
      },
      {
        headTitle: 'Role',
        field: 'role'
      },
      {
        headTitle: '',
        Component: () => {
          return <Chip text={t('Invited')} color={CHIP_COLORS.warning} />
        },
        style: {
          display: 'flex',
          justifyContent: 'flex-end'
        }
      },
      {
        Component: item => <ActionsDropdown closeOnInsideClick options={getInvitesActionsDropdownOptions(item)} />,
        style: {
          minWidth: listItemsTableColumnsMinWidth.dropdown,
          maxWidth: listItemsTableColumnsMinWidth.dropdown,
          padding: 0
        }
      }
    ],
    [t, getInvitesActionsDropdownOptions]
  )

  const skeletonProps = useMemo(() => ({ cols: invitesListColumns }), [invitesListColumns])

  return (
    <ItemsLoading
      className={commonClasses.itemList}
      itemsLength={invitesLists.length}
      SkeletonComponent={SkeletonListRow}
      isLoadingSelector={invitesIsLoadingSelector}
      wasLoadedSelector={invitesWasLoadedSelector}
      errorSelector={invitesErrorSelector}
      skeletonProps={{ cols: invitesListColumns }}
      ContentWrapper={PageSection}
      isEmptyNoDataMessage
    >
      <List
        cols={invitesListColumns}
        listData={invitesLists}
        itemUpdatingId={invitationLoading ? invitationId : ''}
        itemDeletingId={deleteInvitationLoading ? deleteInvitationId : ''}
        Skeleton={<SkeletonListRow {...skeletonProps} key={uuidv4()} />}
      />
    </ItemsLoading>
  )
}

export default InvitesList
